import React, { useState, useEffect } from "react";
import axios from "axios";
import configuration from "../config.js";
import Layout from "../components/layout/InvoiceLayout.jsx";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { 
  Grid, Paper, Typography, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow 
} from "@mui/material";

const InvoiceReports = () => {
  const [data, setData] = useState({ first: [], second: [], third: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = configuration;

  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customerRes, inventoryRes, vendorRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/v1/customer/${selectedCompanyId}`),
          axios.get(`${API_BASE_URL}/api/v1/getInventory/${selectedCompanyId}`),
          axios.get(`${API_BASE_URL}/api/v1/getvenders/${selectedCompanyId}`),
        ]);

        // Ensure we're getting arrays from the API responses
        const customersData = Array.isArray(customerRes.data) ? customerRes.data : 
                             (customerRes.data.customers || customerRes.data.data || []);
        
        const inventoryData = Array.isArray(inventoryRes.data) ? inventoryRes.data : 
                             (inventoryRes.data.inventory || inventoryRes.data.data || []);
        
        const vendorsData = Array.isArray(vendorRes.data) ? vendorRes.data : 
                           (vendorRes.data.vendors || vendorRes.data.data || []);

        setData({
          first: customersData,
          second: inventoryData,
          third: vendorsData,
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL, selectedCompanyId]);

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: 16 }}>
            Loading data...
          </Typography>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Paper elevation={3} sx={{ p: 3, mt: 2, backgroundColor: '#ffebee' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Typography variant="body1">
            Please try refreshing the page or contact support if the issue persists.
          </Typography>
        </Paper>
      </Layout>
    );
  }

  const customers = data.first || [];
  const inventory = data.second || [];
  const vendors = data.third || [];

  // Safety check to ensure all are arrays before rendering
  if (!Array.isArray(customers) || !Array.isArray(inventory) || !Array.isArray(vendors)) {
    return (
      <Layout>
        <Paper elevation={3} sx={{ p: 3, mt: 2, backgroundColor: '#ffebee' }}>
          <Typography variant="h6" color="error">
            Data format error
          </Typography>
          <Typography variant="body1">
            Expected array data but received different format. Please contact support.
          </Typography>
        </Paper>
      </Layout>
    );
  }

  return (
    <Layout>
      <Grid container spacing={2}>
        {/* Customer Data */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Customer List ({customers.length})
            </Typography>
            {customers.length > 0 ? (
              <TableContainer component={Paper} elevation={3} sx={{ mt: 3, p: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Segment</strong></TableCell>
                      <TableCell><strong>Product Purchased</strong></TableCell>
                      <TableCell><strong>Purchase Date</strong></TableCell>
                      <TableCell><strong>Address</strong></TableCell>
                      <TableCell><strong>Payment ID</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer, index) => (
                      <TableRow key={customer._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{customer.name || 'N/A'}</TableCell>
                        <TableCell>{customer.email || 'N/A'}</TableCell>
                        <TableCell>{customer.phone || 'N/A'}</TableCell>
                        <TableCell>{customer.customerSegment || 'N/A'}</TableCell>
                        <TableCell>{customer.productPurchased || 'N/A'}</TableCell>
                        <TableCell>{customer.purchaseDate ? new Date(customer.purchaseDate).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          {customer.address ? 
                            `${customer.address.city || ''}, ${customer.address.state || ''}, ${customer.address.postalCode || ''}` 
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{customer.paymentId || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" sx={{ mt: 2 }}>No customer data available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Inventory Data */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Inventory List ({inventory.length})
            </Typography>
            {inventory.length > 0 ? (
              <TableContainer component={Paper} elevation={3} sx={{ mt: 3, p: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Supplier</strong></TableCell>
                      <TableCell><strong>Purchase Date</strong></TableCell>
                      <TableCell><strong>Quantity In Stock</strong></TableCell>
                      <TableCell><strong>Reorder Level</strong></TableCell>
                      <TableCell><strong>Warehouse Location</strong></TableCell>
                      <TableCell><strong>Total Cost</strong></TableCell>
                      <TableCell><strong>Discount</strong></TableCell>
                      <TableCell><strong>GST Rate</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventory.map((item, index) => (
                      <TableRow key={item._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.name || 'N/A'}</TableCell>
                        <TableCell>{item.category || 'N/A'}</TableCell>
                        <TableCell>{item.supplier || 'N/A'}</TableCell>
                        <TableCell>{item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>{item.quantityInStock || 'N/A'}</TableCell>
                        <TableCell>{item.reorderLevel || 'N/A'}</TableCell>
                        <TableCell>{item.warehouseLocation || 'N/A'}</TableCell>
                        <TableCell>{item.totalCost || 'N/A'}</TableCell>
                        <TableCell>{item.discount || 'N/A'}</TableCell>
                        <TableCell>{item.gstRate ? `${item.gstRate}%` : 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" sx={{ mt: 2 }}>No inventory data available</Typography>
            )}
          </Paper>
        </Grid>

        {/* Vendor Data */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Vendor List ({vendors.length})
            </Typography>
            {vendors.length > 0 ? (
              <TableContainer component={Paper} elevation={3} sx={{ mt: 3, p: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Company Name</strong></TableCell>
                      <TableCell><strong>Contact Person</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Services Provided</strong></TableCell>
                      <TableCell><strong>Ratings</strong></TableCell>
                      <TableCell><strong>Contract Start Date</strong></TableCell>
                      <TableCell><strong>Contract End Date</strong></TableCell>
                      <TableCell><strong>Payment Terms</strong></TableCell>
                      <TableCell><strong>Address</strong></TableCell>
                      <TableCell><strong>GST Number</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendors.map((vendor, index) => (
                      <TableRow key={vendor._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{vendor.companyName || 'N/A'}</TableCell>
                        <TableCell>{vendor.contactPerson || 'N/A'}</TableCell>
                        <TableCell>{vendor.email || 'N/A'}</TableCell>
                        <TableCell>{vendor.phone || 'N/A'}</TableCell>
                        <TableCell>{vendor.servicesProvided || 'N/A'}</TableCell>
                        <TableCell>{vendor.ratings || 'N/A'}</TableCell>
                        <TableCell>{vendor.contractDetails?.startDate ? 
                          new Date(vendor.contractDetails.startDate).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>{vendor.contractDetails?.endDate ? 
                          new Date(vendor.contractDetails.endDate).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>{vendor.contractDetails?.paymentTerms || 'N/A'}</TableCell>
                        <TableCell>
                          {vendor.address ? 
                            `${vendor.address.city || ''}, ${vendor.address.state || ''}, ${vendor.address.postalCode || ''}` 
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{vendor.gstNumber || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" sx={{ mt: 2 }}>No vendor data available</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default InvoiceReports;
